import { Node } from 'reactflow'

import { NodeTypeEnum } from '@ui/interfaces'
import { isEqual } from 'lodash'
import { create } from 'zustand'

import { usePipelinesStore } from '../pipelines/PipelinesStore'
import { useNodeAndEdgeStore } from './NodeAndEdgeStore'

interface PipelineSnapshot {
  nodes: Node[]
  pipelineName: string
  // Add any other relevant pipeline properties here
}

interface PipelineModificationState {
  isModified: boolean
  initialSnapshot: PipelineSnapshot | null
  setModified: (modified: boolean) => void
  takeSnapshot: () => void
  checkModifications: () => void
}

// We only want to compare the nodes that are not placeholders and keep their important properties
const filterNodes = (nodes: Node[]) =>
  nodes
    .filter((node) => node.type !== NodeTypeEnum.PLACEHOLDER)
    .map((node) => ({
      id: node.id,
      ...node.data,
    })) as Node[]

export const usePipelineModificationStore = create<PipelineModificationState>((set, get) => ({
  isModified: false,
  initialSnapshot: null,
  setModified: (modified) => set({ isModified: modified }),
  takeSnapshot: () => {
    const pipelinesStore = usePipelinesStore.getState()
    const nodeAndEdgeStore = useNodeAndEdgeStore.getState()

    if (pipelinesStore.selectedPipeline) {
      const snapshot: PipelineSnapshot = {
        nodes: filterNodes(nodeAndEdgeStore.nodes),
        pipelineName: pipelinesStore.selectedPipeline.name,
      }
      set({ initialSnapshot: snapshot, isModified: false })
    }
  },
  checkModifications: () => {
    const pipelinesStore = usePipelinesStore.getState()
    const nodeAndEdgeStore = useNodeAndEdgeStore.getState()
    const { initialSnapshot } = get()

    if (!initialSnapshot || !pipelinesStore.selectedPipeline) {
      set({ isModified: false })
      return
    }

    const currentSnapshot: PipelineSnapshot = {
      nodes: filterNodes(nodeAndEdgeStore.nodes),
      pipelineName: pipelinesStore.selectedPipeline.name,
    }

    const isModified = !isEqual(initialSnapshot, currentSnapshot)
    set({ isModified })
  },
}))
